import { Inject, Injectable } from '@angular/core';
import { WEB_PRESERVED_STORAGE_KEYS } from '@core/auth';
import {
  WA_LOCAL_STORAGE,
  WA_SESSION_STORAGE,
  WA_WINDOW,
} from '@ng-web-apis/common';
import {
  readGuarded as read,
  removeGuarded as remove,
  writeGuarded as write,
} from '../../services/guarded-storage';

const TAB_ID_KEY = 'photo_station_tab_id';
const TAB_WORKSTATION_KEY = 'photo_station_tab_workstation';

/** The API rejects any id that is not a UUID, so a stored non-UUID is corrupt. */
const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Which photo workstation this browser and this tab work at (RIEG-274), and the
 * ids the workstation lock is keyed on. Keys are not per user: the workstation
 * belongs to the PC, not to whoever is logged in.
 *
 * Two levels:
 *
 * - **Browser** — `localStorage`: the browser id and the last selected
 *   workstation. The auth service's web wipe keeps both, which is why their
 *   key names live in `@core/auth`.
 * - **Tab** — `sessionStorage`: the tab id and the tab's workstation. Browsers
 *   copy session storage into a duplicated tab, which would give the copy the
 *   original's tab id. So both values are taken *out* of session storage at
 *   construction, held in memory while the page lives and written back on
 *   `pagehide`: a reload finds them again, a duplicate made meanwhile finds
 *   nothing and starts as a new tab. That only holds once the service has
 *   been constructed in the page, so it has to be created at app start, not
 *   first on entering the photo station.
 *
 * Storage access is guarded: a browser with storage disabled falls back to
 * memory for this page's lifetime rather than breaking the view.
 */
@Injectable({ providedIn: 'root' })
export class PhotoStationMemoryService {
  readonly browserId: string;
  readonly tabId: string;
  private tabStation: string | null;

  constructor(
    @Inject(WA_LOCAL_STORAGE) private readonly local: Storage,
    @Inject(WA_SESSION_STORAGE) private readonly session: Storage,
    @Inject(WA_WINDOW) window: Window
  ) {
    const storedBrowserId = read(local, WEB_PRESERVED_STORAGE_KEYS.browserId);
    if (storedBrowserId !== null && UUID_RE.test(storedBrowserId)) {
      this.browserId = storedBrowserId;
    } else {
      this.browserId = window.crypto.randomUUID();
      write(local, WEB_PRESERVED_STORAGE_KEYS.browserId, this.browserId);
    }

    const storedTabId = this.takeFromSession(TAB_ID_KEY);
    this.tabId =
      storedTabId !== null && UUID_RE.test(storedTabId)
        ? storedTabId
        : window.crypto.randomUUID();
    this.tabStation = nonEmpty(this.takeFromSession(TAB_WORKSTATION_KEY));

    window.addEventListener('pagehide', () => this.writeBack());
    // A page restored from the back/forward cache is not constructed again,
    // so what pagehide wrote back would stay for a later duplicate to copy.
    window.addEventListener('pageshow', (event) => {
      if ((event as PageTransitionEvent).persisted) {
        this.takeFromSession(TAB_ID_KEY);
        this.takeFromSession(TAB_WORKSTATION_KEY);
      }
    });
  }

  /** The workstation this tab last held, or `null`. */
  tabWorkstation(): string | null {
    return this.tabStation;
  }

  /**
   * The workstation last selected in any tab of this browser, or `null`. Read
   * live, since another tab may have chosen since this one loaded.
   */
  browserWorkstation(): string | null {
    return nonEmpty(read(this.local, WEB_PRESERVED_STORAGE_KEYS.lastWorkstation));
  }

  /** Record a selected workstation at both levels. */
  remember(name: string): void {
    this.tabStation = name;
    write(this.local, WEB_PRESERVED_STORAGE_KEYS.lastWorkstation, name);
  }

  /** Drop the remembered workstation at both levels. */
  forget(): void {
    this.tabStation = null;
    remove(this.local, WEB_PRESERVED_STORAGE_KEYS.lastWorkstation);
  }

  private takeFromSession(key: string): string | null {
    const value = read(this.session, key);
    remove(this.session, key);
    return value;
  }

  private writeBack(): void {
    write(this.session, TAB_ID_KEY, this.tabId);
    if (this.tabStation === null) {
      remove(this.session, TAB_WORKSTATION_KEY);
    } else {
      write(this.session, TAB_WORKSTATION_KEY, this.tabStation);
    }
  }
}

function nonEmpty(value: string | null): string | null {
  return value || null;
}
